// Import Router
import {
    createRouter,
    createWebHistory,
    createWebHashHistory,
    isNavigationFailure
} from "vue-router";
import { nextTick } from "vue";

// Import API and Store
import { API } from "@/services/apiInstance";
import { store } from "@/store";
import { appReady } from "@/store";
import * as Sentry from "@sentry/vue";
import { resetApplicationScroll } from "@/services/appScroll";
import { isPreloadRecoveryNavigationError } from "@/services/preloadErrorRecovery";
import { createLoginRedirectRecovery } from "@/services/loginRedirectRecovery";
import { setDocumentTitleForRoute } from "@/services/documentTitle";
import { checkFormsAccess } from "@/services/pageAccess";

import { canAccessReportRoute } from "@/views/reports/reportConfig";
import { usesPlanFeatureScope } from "@/classes/customerPlans";

// Import Feature Flags
import { Flags, hasFeatureFlag, flagsReady } from "@/classes/FeatureFlags";

const IssuesView = () => import("@/views/issues/IssuesView.vue");
const IssueView = () => import("@/views/issues/IssueView.vue");
const IssueCreateView = () => import("@/views/issues/IssueCreateView.vue");

// Complaints
const ComplaintsView = () => import("@/views/complaints/ComplaintsView.vue");
const ComplaintView = () => import("@/views/complaints/ComplaintView.vue");
const ComplaintCreateView = () => import("@/views/complaints/ComplaintCreateView.vue");

// Home & Static Pages
const HomeView = () => import("@/views/pages/HomeView.vue");
const LoginPage = () => import("@/views/pages/LoginPage.vue");
const NotFound = () => import("@/views/pages/NotFound.vue");

// Checks
const CheckFormBuilder = () => import("@/views/checkforms/builder/CheckFormBuilderView.vue");
const ChecksView = () => import("@/views/checks/ChecksView.vue");
const CheckFormsView = () => import("@/views/checkforms/CheckFormsView.vue");
const CheckFormLibrary = () => import("@/views/formlibrary/CheckFormLibrary.vue");

// Intakes Hub
const IntakesView = () => import("@/views/intake/IntakesView.vue");
const UniversalRecordIdentifyView = () => import("@/views/intake/UniversalRecordIdentifyView.vue");

// Meats
const MeatsView = () => import("@/views/intake/meats/MeatsView.vue");
const MeatIntakeView = () => import("@/views/intake/meats/components/MeatIntakeView.vue");

// Packaging
const PackagingView = () => import("@/views/intake/packaging/PackagingView.vue");
const PackagingIntakeView = () =>
    import("@/views/intake/packaging/components/PackagingIntakeView.vue");
const PackagingTrackingView = () => import("@/views/intake/packaging/PackagingTrackingView.vue");

// Ingredients
const IngredientIntakeView = () =>
    import("@/views/intake/ingredients/components/IngredientIntakeView.vue");
const IngredientsView = () => import("@/views/intake/ingredients/IngredientsView.vue");
const IngredientImportView = () =>
    import("@/views/intake/ingredients/import/IngredientImportView.vue");
const IngredientTrackingView = () =>
    import("@/views/intake/ingredients/IngredientTrackingView.vue");
const MeatTrackingView = () => import("@/views/intake/meats/MeatTrackingView.vue");

// Business
const ClientView = () => import("@/views/client/ClientView.vue");
const UserResetPassword = () => import("@/views/users/components/UserResetPassword.vue");
const ProfileView = () => import("@/views/profile/ProfileView.vue");
const UserClockView = () => import("@/views/userclock/UserClockView.vue");
const FilesView = () => import("@/views/files/FilesView.vue");

// Daily Dash
const DailyDash = () => import("@/views/dailydash/DailyDash.vue");

// Deliveries
const DeliveryIntakeForm = () =>
    import("@/views/intake/deliveries/components/DeliveryIntakeView.vue");
const DeliveriesView = () => import("@/views/intake/deliveries/DeliveriesView.vue");

// Audits
const AuditsView = () => import("@/views/audits/AuditsView.vue");
const AuditView = () => import("@/views/audits/AuditView.vue");
const AuditCreateView = () => import("@/views/audits/AuditCreateView.vue");

// DevMode & Admin
const ScannerSetupPage = () => import("@/views/pages/ScannerPage.vue");

// Dispatches
const DispatchesView = () => import("@/views/dispatches/DispatchesView.vue");

// Stock & Products
const StockView = () => import("@/views/stock/StockView.vue");
const WastageView = () => import("@/views/wastage/WastageView.vue");
const RecipesView = () => import("@/views/recipes/RecipesView.vue");
const ProductsView = () => import("@/views/products/ProductsView.vue");
const ProductView = () => import("@/views/products/ProductView.vue");
const ProductionView = () =>
    import("@/views/multi-recipe-production/MultiRecipeProductionView.vue");
const ProductionRunDetailView = () => import("@/views/production-runs/ProductionRunDetailView.vue");

// Reporting
const MassBalanceView = () => import("@/views/intake/massbalance/MassBalanceView.vue");
const ReportsDashboard = () => import("@/views/reports/ReportsDashboard.vue");
const MonitorView = () => import("@/views/monitoring/MonitorView.vue");

// Temperature Logging
const TemperatureView = () => import("@/views/temperatures/TemperatureView.vue");

// Food Records
const FoodRecordsView = () => import("@/views/foodrecords/FoodRecordsView.vue");

// New Features in Dev
const HACCPPlansView = () => import("@/views/haccp/HACCPPlansView.vue");
const HACCPPlanEditView = () => import("@/views/haccp/HACCPPlanEditView.vue");

const LearnForkto = () => import("@/views/learn/forkto/LearnForktoView.vue");
const AllergensView = () => import("@/views/allergens/AllergensView.vue");

// Administrator-maintained staff training matrix
const TrainingView = () => import("@/views/training/TrainingView.vue");

// Rota / Scheduling
const RotaView = () => import("@/views/rota/RotaView.vue");
const MyScheduleView = () => import("@/views/rota/MyScheduleView.vue");

// Announcements (Team Communication)
const ChatView = () => import("@/views/chat/ChatView.vue");
const AnnouncementsView = () => import("@/views/announcements/AnnouncementsView.vue");

const routes = [
    {
        path: "/unavailable",
        name: "PageUnavailable",
        component: () => import("@/views/pages/PageUnavailable.vue"),
        meta: { requiresAuth: true, title: "Page unavailable" }
    },
    {
        path: "/",
        name: "Home",
        component: HomeView,
        meta: { requiresAuth: true, title: "Dashboard" }
    },
    {
        path: "/check-form-builder",
        name: "CheckFormBuilder",
        component: CheckFormBuilder,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "Check form builder",
            featureFlag: Flags.ChecksModule
        }
    },
    ...(import.meta.env.DEV
        ? [
              {
                  path: "/playground",
                  component: () => import("@/views/playground/PlaygroundLayout.vue"),
                  meta: { requiresAuth: true, requiresDev: true, title: "Playground" },
                  children: [
                      {
                          path: "",
                          name: "Playground",
                          component: () => import("@/views/playground/PlaygroundIndex.vue")
                      },
                      {
                          path: "check-form-renderers",
                          name: "PlaygroundCheckFormRenderers",
                          component: () =>
                              import("@/views/playground/CheckFormRendererGallery.vue"),
                          meta: { title: "Playground · Check Form Renderers" }
                      },
                      {
                          path: "retail-planner",
                          name: "PlaygroundRetailPlanner",
                          component: () => import("@/views/playground/RetailPlanner.vue"),
                          meta: { title: "Playground · Retail Planner" }
                      },
                      {
                          path: "qr-menu-posters",
                          name: "PlaygroundQrMenuPosters",
                          component: () => import("@/views/playground/QrMenuPosters.vue"),
                          meta: { title: "Playground · QR Menu Posters" }
                      },
                      {
                          path: "storage",
                          name: "PlaygroundStorageDemo",
                          component: () => import("@/views/playground/StorageDemo.vue"),
                          meta: {
                              title: "Playground · Google Storage",
                              roles: ["admin"]
                          }
                      },
                      {
                          path: "checkform-assign",
                          name: "PlaygroundCheckFormAssign",
                          component: () => import("@/views/playground/CheckFormAssign.vue"),
                          meta: { title: "Playground · Assign check forms" }
                      }
                  ]
              }
          ]
        : []),
    {
        path: "/users",
        redirect: "/settings/users",
        meta: { requiresAuth: true }
    },
    {
        path: "/issues",
        name: "Issues",
        component: IssuesView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.IssuesModule,
            title: "Issues",
            helpUrl: "https://forkto.com/help/issues/"
        }
    },
    {
        path: "/issues/:id",
        name: "Issue",
        component: IssueView,
        meta: { requiresAuth: true, featureFlag: Flags.IssuesModule, title: "Issue" }
    },
    {
        path: "/issues/create",
        name: "IssueCreate",
        component: IssueCreateView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            featureFlag: Flags.IssuesModule,
            title: "New Issue"
        }
    },
    {
        path: "/locations",
        redirect: "/settings/locations",
        meta: { requiresAuth: true }
    },
    {
        path: "/departments",
        redirect: "/settings/departments",
        meta: { requiresAuth: true }
    },
    {
        path: "/intakes",
        name: "IntakesView",
        component: IntakesView,
        meta: {
            requiresAuth: true,
            title: "Intakes & Stock",
            helpUrl: "https://forkto.com/help/intakes/"
        }
    },
    {
        path: "/identify",
        name: "UniversalRecordIdentify",
        component: UniversalRecordIdentifyView,
        meta: {
            requiresAuth: true,
            denyRoles: ["supplier"],
            title: "Identify intake"
        }
    },
    {
        path: "/temperature-logging",
        name: "TemperatureView",
        component: TemperatureView,
        meta: {
            requiresAuth: true,
            title: "Temperature logging",
            featureFlag: Flags.TemperatureLogging,
            helpUrl: "https://forkto.com/help/temperature-logging/"
        }
    },
    {
        path: "/meats/:id",
        name: "MeatView",
        component: MeatTrackingView,
        meta: { requiresAuth: true, title: "Meat" }
    },
    {
        path: "/ingredients/:id",
        name: "IngredientView",
        component: IngredientTrackingView,
        meta: { requiresAuth: true, title: "Ingredient" }
    },
    {
        path: "/packaging/:id",
        name: "PackagingItemView",
        component: PackagingTrackingView,
        meta: { requiresAuth: true, title: "Packaging Item" }
    },
    {
        path: "/intake/meat",
        name: "MeatIntakeView",
        component: MeatIntakeView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "New Meat Intake",
            featureFlag: Flags.MeatModule
        }
    },
    {
        path: "/ingredients",
        name: "IngredientsView",
        component: IngredientsView,
        meta: {
            requiresAuth: true,
            title: "Ingredients",
            featureFlag: Flags.IngredientsModule,
            helpUrl: "https://forkto.com/help/ingredients-intake/"
        }
    },
    {
        path: "/intake/ingredient",
        name: "IngredientIntakeView",
        component: IngredientIntakeView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "New Ingredient Intake",
            featureFlag: Flags.IngredientsModule
        }
    },
    {
        path: "/intake/ingredients/import",
        name: "IngredientImportView",
        component: IngredientImportView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "Bulk ingredient import",
            featureFlag: Flags.IngredientImport
        }
    },
    // Packaging Routes
    {
        path: "/packaging",
        name: "PackagingView",
        component: PackagingView,
        meta: {
            featureFlag: Flags.PackagingModule,
            requiresAuth: true,
            title: "Packaging",
            helpUrl: "https://forkto.com/help/packaging-intake/"
        }
    },
    {
        path: "/intake/packaging",
        name: "PackagingIntakeForm",
        component: PackagingIntakeView,
        meta: {
            featureFlag: Flags.PackagingModule,
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "New Packaging Intake"
        }
    },
    {
        path: "/deliveries",
        name: "DeliveriesView",
        component: DeliveriesView,
        meta: {
            requiresAuth: true,
            title: "Deliveries",
            featureFlag: Flags.DeliveriesModule,
            helpUrl: "https://forkto.com/help/deliveries/"
        }
    },
    {
        path: "/intake/delivery",
        name: "DeliveryIntakeForm",
        component: DeliveryIntakeForm,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "New Delivery",
            featureFlag: Flags.DeliveriesModule
        }
    },
    {
        path: "/client-view",
        redirect: "/settings",
        meta: { requiresAuth: true }
    },
    {
        path: "/settings/:section?",
        name: "Settings",
        component: ClientView,
        meta: {
            requiresAuth: true,
            title: "Settings",
            roles: ["admin", "auditor"],
            helpUrl: "https://forkto.com/help/client-profile/"
        }
    },
    {
        path: "/meats",
        name: "MeatsView",
        component: MeatsView,
        meta: {
            requiresAuth: true,
            title: "Meats",
            featureFlag: Flags.MeatModule,
            helpUrl: "https://forkto.com/help/meat-intake/"
        }
    },
    {
        path: "/mass-balance/:id",
        name: "MassBalanceView",
        component: MassBalanceView,
        meta: {
            requiresAuth: true,
            title: "Mass Balance"
        }
    },
    {
        path: "/suppliers",
        redirect: "/settings/suppliers",
        meta: { requiresAuth: true }
    },
    {
        path: "/checks",
        name: "ChecksView",
        component: ChecksView,
        meta: {
            requiresAuth: true,
            title: "Checks",
            featureFlag: Flags.ChecksModule,
            helpUrl: "https://forkto.com/help/checks/"
        }
    },
    {
        path: "/checks/:id",
        name: "CheckRecord",
        component: () => import("@/views/checks/CheckRecordView.vue"),
        meta: {
            requiresAuth: true,
            title: "Check Record",
            roles: ["admin", "auditor"],
            featureFlag: Flags.ChecksModule
        }
    },
    {
        path: "/check-forms",
        name: "CheckFormsView",
        component: CheckFormsView,
        meta: {
            requiresAuth: true,
            title: "Check forms",
            ...checkFormsAccess,
            helpUrl: "https://forkto.com/help/check-form-builder-management/"
        }
    },
    {
        path: "/daily-dash",
        name: "DailyDashView",
        component: DailyDash,
        meta: {
            requiresAuth: true,
            title: "Daily dashboard",
            roles: ["admin", "auditor"],
            helpUrl: "https://forkto.com/help/daily-dashboard/"
        }
    },
    {
        path: "/check-form-library",
        name: "CheckFormLibrary",
        component: CheckFormLibrary,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "Check form library",
            featureFlag: Flags.ChecksModule
        }
    },
    {
        path: "/recipes",
        name: "RecipesView",
        component: RecipesView,
        meta: {
            requiresAuth: true,
            title: "Recipes",
            featureFlag: Flags.RecipesModule,
            helpUrl: "https://forkto.com/help/recipes/"
        }
    },
    {
        path: "/stock",
        name: "StockView",
        component: StockView,
        meta: {
            requiresAuth: true,
            title: "Stock",
            featureFlag: Flags.StockModule,
            helpUrl: "https://forkto.com/help/stock-management/"
        }
    },
    {
        path: "/wastage",
        name: "WastageView",
        component: WastageView,
        meta: {
            requiresAuth: true,
            title: "Wastage",
            featureFlag: Flags.WastageModule,
            helpUrl: "https://forkto.com/help/wastage/"
        }
    },
    {
        path: "/reports",
        meta: {
            requiresAuth: true,
            title: "Reports",
            roles: ["admin", "auditor"],
            helpUrl: "https://forkto.com/help/reports/"
        },
        children: [
            {
                path: "tasks",
                name: "ReportTasks",
                component: () => import("@/views/reports/TasksReport.vue"),
                meta: { title: "Tasks", roles: ["admin", "user"] }
            },
            {
                path: "",
                name: "Reports",
                meta: { roles: ["admin", "auditor", "user"] },
                component: ReportsDashboard
            },
            {
                path: "meats",
                name: "ReportMeats",
                component: () => import("@/views/reports/MeatsReport.vue"),
                meta: { title: "Meat Intakes" }
            },
            {
                path: "ingredients",
                name: "ReportIngredients",
                component: () => import("@/views/reports/IngredientsReport.vue"),
                meta: { title: "Ingredients" }
            },
            {
                path: "packaging",
                name: "ReportPackaging",
                component: () => import("@/views/reports/PackagingReport.vue"),
                meta: { title: "Packaging" }
            },
            {
                path: "products",
                name: "ReportProducts",
                component: () => import("@/views/reports/ProductsReport.vue"),
                meta: { title: "Products" }
            },
            {
                path: "suppliers",
                name: "ReportSuppliers",
                component: () => import("@/views/reports/SuppliersReport.vue"),
                meta: { title: "Suppliers" }
            },
            {
                path: "stock",
                name: "ReportStock",
                component: () => import("@/views/reports/StockReport.vue"),
                meta: { title: "Stock" }
            },
            {
                path: "checks",
                name: "ReportChecks",
                component: () => import("@/views/reports/ChecksReport.vue"),
                meta: { title: "Checks" }
            },
            {
                path: "temperatures",
                name: "ReportTemperatures",
                component: () => import("@/views/reports/TemperaturesReport.vue"),
                meta: { title: "Temperature Logs" }
            },
            {
                path: "food-records",
                name: "ReportFoodRecords",
                component: () => import("@/views/reports/FoodRecordsReport.vue"),
                meta: { title: "Food records" }
            },
            {
                path: "user-clock-events",
                name: "ReportUserClockEvents",
                component: () => import("@/views/reports/UserClockEventsReport.vue"),
                meta: { title: "User Clock Events" }
            },
            {
                path: "production-runs",
                name: "ReportProductionRuns",
                component: () => import("@/views/reports/ProductionRunsReport.vue"),
                meta: { title: "Production Runs" },
                beforeEnter: (_to, _from, next) => {
                    if (!hasFeatureFlag(Flags.MultiRecipeProduction)) {
                        next("/");
                        return;
                    }
                    next();
                }
            },
            {
                path: "dispatches",
                name: "ReportDispatches",
                component: () => import("@/views/reports/DispatchesReport.vue"),
                meta: { title: "Dispatches" },
                beforeEnter: (_to, _from, next) => {
                    if (!hasFeatureFlag(Flags.DispatchModule)) {
                        next("/");
                        return;
                    }
                    next();
                }
            },
            {
                path: "payroll",
                name: "ReportPayroll",
                component: () => import("@/views/reports/PayrollReport.vue"),
                // F15: payroll is admin-only and gated behind WagesModule. The
                // parent /reports route also allows the auditor role, so without
                // this an auditor — or an admin on a tenant without WagesModule —
                // could direct-nav here.
                meta: { title: "Payroll", roles: ["admin"], featureFlag: Flags.WagesModule }
            },
            {
                path: "product-trace/:id",
                name: "ReportProductTrace",
                component: () => import("@/views/reports/ProductTraceReport.vue"),
                props: true,
                meta: { title: "Product Trace" }
            }
        ]
    },
    {
        path: "/monitoring",
        name: "Monitor",
        component: MonitorView,
        meta: {
            requiresAuth: true,
            title: "Sensor monitoring",
            roles: ["admin", "auditor"],
            featureFlag: Flags.SensorModule,
            helpUrl: "https://forkto.com/help/sensor-monitoring/"
        }
    },
    {
        path: "/scanner-setup",
        name: "ScannerSetup",
        component: ScannerSetupPage,
        meta: {
            requiresAuth: true,
            title: "Scanner setup"
        }
    },

    {
        path: "/user-files",
        name: "FilesView",
        component: FilesView,
        meta: {
            requiresAuth: true,
            title: "Files",
            featureFlag: Flags.FilesModule,
            helpUrl: "https://forkto.com/help/files-management/"
        }
    },
    {
        path: "/login",
        name: "LoginPage",
        component: LoginPage,
        meta: { requiresAuth: false, publicLayout: true, title: "Login" }
    },
    {
        path: "/profile",
        name: "Profile",
        component: ProfileView,
        meta: { requiresAuth: true, title: "Profile", helpUrl: "https://forkto.com/help/profile/" }
    },
    {
        path: "/products",
        name: "ProductsView",
        component: ProductsView,
        meta: {
            requiresAuth: true,
            title: "Products",
            featureFlag: Flags.ProductsModule,
            helpUrl: "https://forkto.com/help/products/"
        }
    },
    {
        path: "/products/:id",
        name: "ProductView",
        component: ProductView,
        meta: {
            requiresAuth: true,
            title: "Product",
            featureFlag: Flags.ProductsModule
        }
    },
    {
        path: "/production",
        name: "ProductionView",
        component: ProductionView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "Production",
            // Universal produce flow — every recipe card routes here, so it is
            // gated by ProductsModule (as the old single-product screen was)
            // rather than the MultiRecipeProduction planning flag.
            featureFlag: Flags.ProductsModule,
            helpUrl: "https://forkto.com/help/production/"
        }
    },
    {
        path: "/multi-recipe-production",
        redirect: { name: "ProductionView" }
    },
    {
        path: "/production-runs",
        name: "ProductionRunsView",
        redirect: { name: "ProductsView" }
    },
    {
        path: "/batches/:key",
        name: "BatchJourney",
        component: () => import("@/views/batch-journey/BatchJourneyView.vue"),
        meta: { requiresAuth: true, title: "Batch journey", featureFlag: Flags.ProductsModule }
    },
    {
        path: "/production-runs/:id",
        name: "ProductionRunDetailView",
        component: ProductionRunDetailView,
        meta: {
            requiresAuth: true,
            title: "Production Run",
            featureFlag: Flags.MultiRecipeProduction
        }
    },
    {
        path: "/dispatches",
        name: "DispatchesView",
        component: DispatchesView,
        meta: {
            requiresAuth: true,
            title: "Dispatches",
            featureFlag: Flags.DispatchModule,
            helpUrl: "https://forkto.com/help/dispatches/"
        }
    },
    {
        path: "/reset-password",
        name: "ResetPassword",
        component: UserResetPassword,
        meta: { requiresAuth: false, publicLayout: true, title: "Reset Password" }
    },
    {
        path: "/user-clock",
        name: "UserClockView",
        component: UserClockView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            title: "Clock In / Clock Out",
            featureFlag: Flags.UserClockModule,
            helpUrl: "https://forkto.com/help/clock-in-clock-out/"
        }
    },
    {
        path: "/allergens",
        name: "AllergensView",
        component: AllergensView,
        meta: {
            requiresAuth: true,
            title: "Allergens",
            featureFlag: Flags.AllergensModule,
            helpUrl: "https://forkto.com/help/allergens/"
        }
    },
    {
        path: "/menus",
        name: "MenusView",
        component: () => import("@/views/allergens/MenusView.vue"),
        meta: {
            requiresAuth: true,
            title: "Menus",
            featureFlag: Flags.MenusModule,
            helpUrl: "https://forkto.com/help/menus/"
        }
    },
    {
        path: "/menus/:id",
        name: "MenuEditorView",
        component: () => import("@/views/allergens/MenuEditorView.vue"),
        meta: {
            requiresAuth: true,
            title: "Menu Editor",
            featureFlag: Flags.MenusModule,
            helpUrl: "https://forkto.com/help/menus/"
        }
    },
    {
        path: "/food-records",
        name: "FoodRecordsView",
        component: FoodRecordsView,
        meta: {
            featureFlag: Flags.FoodRecords,
            requiresAuth: true,
            title: "Food Temperature Records",
            helpUrl: "https://forkto.com/help/food-temperature-records/"
        }
    },
    // HACCP — multi-plan editor with snapshot-on-publish revisioning.
    {
        path: "/haccp",
        name: "HACCPPlans",
        component: HACCPPlansView,
        meta: {
            requiresAuth: true,
            title: "HACCP Plans",
            featureFlag: Flags.HACCPModule,
            helpUrl: "https://forkto.com/help/haccp/"
        }
    },
    {
        path: "/haccp/:id",
        name: "HACCPPlanEdit",
        component: HACCPPlanEditView,
        meta: {
            requiresAuth: true,
            title: "HACCP Plan",
            featureFlag: Flags.HACCPModule
        }
    },
    {
        path: "/learn-forkto",
        name: "LearnForkto",
        component: LearnForkto,
        meta: {
            requiresAuth: true,
            title: "Learn Forkto"
        }
    },
    {
        path: "/audits",
        name: "Audits",
        component: AuditsView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.AuditModule,
            title: "Audits",
            helpUrl: "https://forkto.com/help/audits/"
        }
    },
    {
        path: "/audits/create",
        name: "AuditCreate",
        component: AuditCreateView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            featureFlag: Flags.AuditModule,
            title: "New Audit"
        }
    },
    {
        path: "/audits/:id",
        name: "Audit",
        component: AuditView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.AuditModule,
            title: "Audit"
        }
    },
    {
        path: "/audits/:id/edit",
        name: "AuditEdit",
        component: AuditCreateView,
        meta: {
            requiresAuth: true,
            denyRoles: ["auditor"],
            featureFlag: Flags.AuditModule,
            title: "Edit Audit"
        }
    },
    {
        path: "/complaints",
        name: "Complaints",
        component: ComplaintsView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.ComplaintsModule,
            title: "Complaints",
            roles: ["admin", "auditor", "supplier"],
            helpUrl: "https://forkto.com/help/complaints/"
        }
    },
    {
        path: "/complaints/create",
        name: "ComplaintCreate",
        component: ComplaintCreateView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.ComplaintsModule,
            title: "New Complaint",
            roles: ["admin", "supplier"]
        }
    },
    {
        path: "/complaints/:id",
        name: "Complaint",
        component: ComplaintView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.ComplaintsModule,
            title: "Complaint",
            roles: ["admin", "auditor", "supplier"]
        }
    },
    {
        path: "/complaints/:id/edit",
        name: "ComplaintEdit",
        component: () => import("@/views/complaints/ComplaintEditView.vue"),
        meta: {
            requiresAuth: true,
            featureFlag: Flags.ComplaintsModule,
            title: "Edit Complaint",
            roles: ["admin", "supplier"]
        }
    },
    {
        path: "/labels",
        name: "Labels",
        component: () => import("@/views/labels/LabelsView.vue"),
        meta: {
            requiresAuth: true,
            featureFlag: Flags.LabelTemplates,
            title: "Labels",
            roles: ["admin"]
        }
    },
    {
        path: "/training",
        name: "Training",
        component: TrainingView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.TrainingModule,
            roles: ["admin"],
            title: "Training"
        }
    },
    {
        path: "/chat",
        name: "Chat",
        component: ChatView,
        meta: {
            requiresAuth: true,
            roles: ["admin", "user"],
            title: "Team chat",
            featureFlag: Flags.TeamChatModule,
            unavailableRoute: "PageUnavailable"
        }
    },
    {
        path: "/announcements/:id",
        name: "AnnouncementDetail",
        component: () => import("@/views/announcements/AnnouncementDetailView.vue"),
        meta: { requiresAuth: true, roles: ["admin", "user"], title: "Team announcement" }
    },
    {
        path: "/announcements",
        name: "Announcements",
        component: AnnouncementsView,
        meta: {
            requiresAuth: true,
            roles: ["admin"],
            title: "Announcements",
            helpUrl: "https://forkto.com/help/announcements/"
        }
    },
    {
        path: "/rota",
        name: "Rota",
        component: RotaView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.RotaModule,
            title: "Staff Rota",
            roles: ["admin"],
            helpUrl: "https://forkto.com/help/staff-rota/"
        }
    },
    {
        path: "/my-schedule",
        name: "MySchedule",
        component: MyScheduleView,
        meta: {
            requiresAuth: true,
            featureFlag: Flags.RotaModule,
            title: "My Schedule",
            helpUrl: "https://forkto.com/help/staff-rota/"
        }
    },
    {
        path: "/menu/:publicToken",
        name: "PublicMenu",
        component: () => import("@/views/public/PublicMenuView.vue"),
        meta: { public: true, publicLayout: true, requiresAuth: false, title: "Menu" }
    },
    {
        path: "/:catchAll(.*)",
        name: "NotFound",
        component: NotFound,
        meta: { requiresAuth: false, title: "Page Not Found" }
    }
];

const router = createRouter({
    history:
        typeof window === "undefined" ||
        (window.cordova != null && window.cordova?.platformId !== "browser")
            ? createWebHashHistory(import.meta.env.BASE_URL)
            : createWebHistory(import.meta.env.BASE_URL),
    routes
});

const loginRedirectRecovery = createLoginRedirectRecovery({
    resolveHref: (target) => router.resolve(target).href,
    flush: (timeout) => Sentry.flush(timeout)
});

router.beforeEach(async (to, _from, next) => {
    store.commit("setRouteLoading", true);

    // Backwards-compat: legacy /reports?type=X URLs (used before the
    // route-per-report refactor) redirect to the new path-based routes,
    // preserving any other query params (startDate, endDate, search, etc.).
    if (to.path === "/reports" && to.query.type) {
        const type = String(to.query.type).toLowerCase().replace(/_/g, "-");

        const { type: _type, id, ...restQuery } = to.query;
        if (type === "product-trace" && id) {
            return next({
                name: "ReportProductTrace",
                params: { id },
                query: restQuery,
                replace: true
            });
        }
        return next({ path: `/reports/${type}`, query: restQuery, replace: true });
    }

    // Public routes (token-gated, no login required) bypass all checks.
    if (to.meta.public) {
        next();
        return;
    }

    // Wait for initial app bootstrap (auth, client, settings) before gating.
    await appReady;

    if (store.state.authUnlockRequired) {
        if (to.name === "LoginPage") {
            next();
            return;
        }

        if (to.meta.requiresAuth) {
            loginRedirectRecovery.redirect(
                next,
                "/login?unlock=biometric&redirect=" + encodeURIComponent(to.fullPath),
                to
            );
            return;
        }
    }

    const authCheck = await API.checkAuthWithoutLogout();
    const userRole = store.getters.userRole();

    if (!authCheck) {
        // Access token rejected — if we still have session context, try a refresh.
        if (store.state.token && store.getters.userID()) {
            try {
                await API.Auth.refreshAccessToken();
                next();
            } catch (error) {
                Sentry.addBreadcrumb({
                    category: "auth",
                    message: "Forced logout: refresh failed in router guard",
                    level: "warning",
                    data: { route: to.fullPath, error: error.message }
                });
                await API.Auth.clearAuth();
                loginRedirectRecovery.redirect(
                    next,
                    "/login?redirect=" + encodeURIComponent(to.fullPath),
                    to
                );
            }
            return;
        }

        if (to.meta.requiresAuth) {
            await API.Auth.clearAuth();
            loginRedirectRecovery.redirect(
                next,
                "/login?redirect=" + encodeURIComponent(to.fullPath),
                to
            );
            return;
        }
    }

    if (to.meta.roles && !to.meta.roles.includes(userRole)) {
        next(
            to.meta.unavailableRoute
                ? {
                      name: to.meta.unavailableRoute,
                      query: { page: to.meta.title, reason: "role" },
                      replace: true
                  }
                : "/"
        );
        return;
    }

    // denyRoles blocks only the listed roles (deny-list), leaving every other
    // role untouched — used to keep auditors out of create/edit form routes
    // without having to enumerate every allowed role.
    if (to.meta.denyRoles && to.meta.denyRoles.includes(userRole)) {
        next("/");
        return;
    }

    if (to.meta.requiresDev && !store.getters.canAccessDevMode()) {
        next("/");
        return;
    }

    if (to.meta.featureFlag) {
        await flagsReady;
        if (!hasFeatureFlag(to.meta.featureFlag)) {
            Sentry.addBreadcrumb({
                category: "navigation",
                message: "Feature flag denied navigation",
                level: "info",
                data: { route: to.fullPath, flag: to.meta.featureFlag, role: userRole }
            });
            next(
                to.meta.unavailableRoute
                    ? {
                          name: to.meta.unavailableRoute,
                          query: { page: to.meta.title, reason: "feature" },
                          replace: true
                      }
                    : "/"
            );
            return;
        }
    }

    if (usesPlanFeatureScope(store.state.client, store.state.devOverrides)) {
        await flagsReady;
        if (
            !canAccessReportRoute(to.name, {
                has: hasFeatureFlag,
                role: (role) => role === userRole,
                planScoped: true
            })
        )
            return next("/");
    }

    // Logged-in users hitting the login page get bounced to their redirect or home.
    if (
        !store.state.authUnlockRequired &&
        !to.meta.requiresAuth &&
        authCheck &&
        to.name === "LoginPage"
    ) {
        next(to.query.redirect || "/");
        return;
    }

    // Unknown route + not logged in → send to login (preserving target as redirect).
    if (to.name === "NotFound" && !authCheck) {
        loginRedirectRecovery.redirect(next, "/login?redirect=" + encodeURIComponent(to.fullPath), to);
        return;
    }

    next();
});

router.afterEach(async (to, from) => {
    store.commit("setRouteLoading", false);
    // Keep the page title aligned with the committed URL. Setting it before
    // guards finish can expose an attempted route's title alongside the final
    // redirected URL to integrations that observe navigation.
    setDocumentTitleForRoute(to);

    // RouterView swaps content inside persistent shell scrollports. Reset those
    // after the new page renders; window-only scroll behavior cannot reach them.
    if (to.path !== from.path) {
        await nextTick();
        resetApplicationScroll();
    }
});

// afterEach does NOT run when a navigation errors (e.g. a lazy route chunk
// 404s after a deploy, or a guard throws), so without this the route-loading
// overlay set in beforeEach covers the content area forever while the sidebar
// stays usable. preloadErrorRecovery owns the reload-on-stale-chunk path; this
// handler is the terminal fallback: unstick the UI and report the failure.
router.onError((error, to) => {
    store.commit("setRouteLoading", false);
    // Redirects, cancellations, and duplicate navigations are expected router
    // control flow. Vue Router strips their messages in production, so reporting
    // them produces misleading "No error message" issues such as #145911025.
    if (isNavigationFailure(error)) return;
    if (isPreloadRecoveryNavigationError(error)) return;
    Sentry.captureException(error, {
        contexts: {
            navigation: { to: to?.fullPath },
            login_redirect: loginRedirectRecovery.getDiagnostics(error)
        }
    });
    void loginRedirectRecovery.recover(error).catch((recoveryError) => {
        Sentry.captureException(recoveryError, {
            contexts: { navigation: { to: to?.fullPath, recovery: "login-redirect" } }
        });
    });
});

export default router;
